import {
  GroupTextField,
  GroupTextFieldRow,
} from "@elaniintc/goes-design-system";
import {
  ExpedienteActionSelector,
  type ActionItem,
} from "./components/action-selector";
import { EntitySelector } from "./components/entity-selector";
import { RegistryIdInput } from "./components/registry-id-input";
import { ExpedienteCreateForm } from "./forms/create-form";
import { cn } from "@/utils/theme";
import { ExpedienteUpdateForm } from "./forms/update-form";
import { ExpedienteLinkForm } from "./forms/link-form";
import { useExpedienteNode } from "./context";
import { useState } from "react";

export const ExpedienteNode = () => {
  // Control which dropdown is open (only one at a time)
  const [openDropdown, setOpenDropdown] = useState<
    "registry-variable" | "action" | null
  >(null);

  const {
    reset,
    selectedAction,
    setSelectedAction,
    // Entity selection
    entities,
    isLoadingEntities,
    selectedEntity,
    setSelectedEntity,
    // Registry ID input
    registryInputMode,
    setRegistryInputMode,
    registryIdValue,
    setRegistryIdValue,
    registryVariable,
    setRegistryVariable,
    inputVariables,
  } = useExpedienteNode();

  const hasRegistryValue =
    registryInputMode === "inp" ? !!registryIdValue.trim() : !!registryVariable;

  const shouldShowForm =
    selectedAction?.id === "create"
      ? selectedEntity && selectedAction
      : selectedEntity && hasRegistryValue && selectedAction;

  return (
    <div className="py-3 flex flex-col gap-3">
      {/* Step 1: Entity Selection */}
      <div className={cn("w-full pt-3 pb-3 px-4")}>
        <GroupTextField
          label="Entidad base"
          required
          helperText="Selecciona la entidad base del expediente digital."
        >
          <GroupTextFieldRow>
            <EntitySelector
              entities={entities}
              selected={selectedEntity}
              onSelect={(entity) => {
                setSelectedEntity(entity);
                // Reset registry and action when entity changes
                setRegistryIdValue("");
                setRegistryVariable(null);
                setSelectedAction(undefined);
                reset();
              }}
              isLoading={isLoadingEntities}
            />
          </GroupTextFieldRow>
        </GroupTextField>
      </div>

      {/* Step 2: Registry ID + Action (only show after entity selected) */}
      {selectedEntity && (
        <div
          className={cn(
            "w-full pb-6 px-4",
            shouldShowForm && "border-b border-neutral-quiet",
          )}
        >
          <GroupTextField
            label="Registro de expediente"
            required={selectedAction?.id !== "create"}
            helperText="Busca el ID del registro dentro de la entidad seleccionada."
          >
            <GroupTextFieldRow>
              <RegistryIdInput
                mode={registryInputMode}
                onModeChange={setRegistryInputMode}
                inputValue={registryIdValue}
                onInputChange={setRegistryIdValue}
                selectedVariable={registryVariable}
                onVariableChange={setRegistryVariable}
                variables={inputVariables}
                disabled={selectedAction?.id === "create"}
                open={openDropdown === "registry-variable"}
                onOpenChange={(isOpen) => {
                  setOpenDropdown(isOpen ? "registry-variable" : null);
                }}
              />
              {/* Action selector */}
              <ExpedienteActionSelector
                disabled={false}
                readonly={false}
                selected={selectedAction}
                onActionSelection={(action: ActionItem) => {
                  setSelectedAction(action);
                  reset();
                }}
                open={openDropdown === "action"}
                onOpenChange={(isOpen) => {
                  setOpenDropdown(isOpen ? "action" : null);
                }}
              />
            </GroupTextFieldRow>
          </GroupTextField>
        </div>
      )}

      {/* For "read" and "deactivate" actions - no additional form, only shows output variables */}
      {shouldShowForm && selectedAction?.id === "create" && (
        <ExpedienteCreateForm />
      )}
      {shouldShowForm && selectedAction?.id === "update" && (
        <ExpedienteUpdateForm />
      )}
      {shouldShowForm && selectedAction?.id === "link_users" && (
        <ExpedienteLinkForm />
      )}
    </div>
  );
};
